import { ExpandedState } from "@tanstack/react-table";
import { EmptyState } from "PFComponents/empty_state";
import { NestedTable } from "PFComponents/table/nested_table";
import { NotificationSetting } from "PFTypes";
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { NotificationRow, NotificationSettingsFormValues } from "./notifications_settings_form.types";
import { BulkEditModal } from "./parts/bulk_edit_modal";
import { BulkSelectOverlay } from "./parts/bulk_select_overlay";
import { useNotificationColumnsGenerator } from "./use_notification_columns_generator";
import { useNotificationRowSelection } from "./use_notification_row_selection";

type NotificationsSettingsFormProps = {
  notificationsSettings: NotificationSetting[];
  formMethods: UseFormReturn<NotificationSettingsFormValues>;
  allRows: NotificationRow[];
  filteredRows: NotificationRow[];
  expandedState: ExpandedState;
  setExpandedState: Dispatch<SetStateAction<ExpandedState>>;
  onSelectionChange?: (selectedSettings: NotificationSetting[]) => void;
  overlayContainerRef?: RefObject<HTMLDivElement | null>;
  showImportantColumn?: boolean;
};

export const NotificationsSettingsForm = ({
  notificationsSettings,
  formMethods,
  allRows,
  filteredRows,
  expandedState,
  setExpandedState,
  onSelectionChange,
  overlayContainerRef,
  showImportantColumn
}: NotificationsSettingsFormProps) => {
  const { t } = useTranslation("notificationsSettings");
  const generateColumns = useNotificationColumnsGenerator(showImportantColumn);
  const { rowSelection, handleRowSelectionChange, selectedSettings, clearSelection } =
    useNotificationRowSelection(notificationsSettings, allRows);

  useEffect(() => {
    onSelectionChange?.(selectedSettings);
  }, [selectedSettings, onSelectionChange]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const childrenTableConfig = useMemo(
    () => ({
      generateColumns,
      enableRowSelection: true,
      enableMultiRowSelection: true,
      onRowSelectionChange: handleRowSelectionChange,
      rowSelection,
      getRowId: ({ rowId }: NotificationRow) => rowId,
      noHover: true,
      disableAutoResizing: true,
      disableInternalScrolling: true,
      disableHeader: true
    }),
    [generateColumns, rowSelection, handleRowSelectionChange]
  );

  const overlay =
    selectedSettings.length > 0 ? (
      <BulkSelectOverlay
        selectedSettings={selectedSettings}
        onClose={clearSelection}
        onEdit={() => setIsEditModalOpen(true)}
      />
    ) : null;

  if (filteredRows.length === 0) {
    return <EmptyState title={t("search.noResults")} />;
  }

  return (
    <FormProvider {...formMethods}>
      <NestedTable<NotificationRow, NotificationRow>
        tableData={filteredRows}
        generateColumns={generateColumns}
        controlledExpanded={expandedState}
        onExpandedChange={setExpandedState}
        enableMultiRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        getRowId={(row) => row.rowId}
        noHover
        disableAutoResizing
        disableInternalScrolling
        rowSelectionConditions={{
          isRowExpandable: (row) => row.original.group !== null
        }}
        childrenTableConfig={childrenTableConfig}
      />
      {isEditModalOpen && selectedSettings.length > 0 && (
        <BulkEditModal
          selectedSettings={selectedSettings}
          withImportantToggle={showImportantColumn}
          onClose={() => setIsEditModalOpen(false)}
          onApply={clearSelection}
        />
      )}
      {overlayContainerRef?.current && overlay ? createPortal(overlay, overlayContainerRef.current) : overlay}
    </FormProvider>
  );
};
